import { useEffect, useState } from 'react';
import axios from 'axios';
import DateInput from './DateInput.jsx';

// Three-stage dialog: paste → review → saved.
// `record` is the customer (needs id for the API + email-mismatch warning).
// `site` is optional — when provided, the parsed configuration attaches
// to that specific site's id, and the mismatch warning compares against
// the site's site_number instead of the customer's legacy value.
// `onSaved` reloads the parent so the new credential rows appear.
export default function BullochEmailParseDialog({ open, record, site, onClose, onSaved }) {
  const [stage, setStage] = useState('paste');   // paste | review | saved
  const [text, setText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const todayISO = () => new Date().toISOString().slice(0, 10);

  // Shared connection fields (one host serves N credentials).
  const [shared, setShared] = useState({
    sftp_protocol: '', sftp_host: '', sftp_port: '',
    date_received: todayISO()
  });

  // Array of { username, password, label } — one entry per cred set the
  // parser detected. Editable so the user can rename labels or fix typos.
  const [credentials, setCredentials] = useState([]);

  const [siteFromSubject, setSiteFromSubject] = useState(null);
  const [savedCount, setSavedCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    setStage('paste');
    setText('');
    setError(null);
    setShared({ sftp_protocol: '', sftp_host: '', sftp_port: '', date_received: todayISO() });
    setCredentials([]);
    setSiteFromSubject(null);
    setSavedCount(0);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !parsing && !saving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, parsing, saving]);

  if (!open) return null;

  async function handleParse() {
    if (!text.trim()) return;
    setParsing(true);
    setError(null);
    try {
      const res = await axios.post('/api/bulloch/parse', { text });
      const sh = res.data.shared || {};
      setShared((prev) => ({
        sftp_protocol: sh.sftp_protocol || '',
        sftp_host:     sh.sftp_host     || '',
        sftp_port:     sh.sftp_port === 0 || sh.sftp_port ? String(sh.sftp_port) : '',
        date_received: prev.date_received || todayISO()
      }));
      // credential_sets is the new shape; fall back to a single-entry
      // synthesised from the legacy `parsed` shape for older servers.
      const sets = Array.isArray(res.data.credential_sets) && res.data.credential_sets.length > 0
        ? res.data.credential_sets
        : [{
            username: res.data.parsed?.sftp_username || '',
            password: res.data.parsed?.sftp_password || '',
            label: ''
          }];
      setCredentials(sets.map((c) => ({
        username: c.username || '',
        password: c.password || '',
        label:    c.label    || ''
      })));
      setSiteFromSubject(res.data.site_number_from_subject || null);
      setStage('review');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setParsing(false);
    }
  }

  function updateShared(field) {
    return (e) => setShared((prev) => ({ ...prev, [field]: e.target.value }));
  }
  function updateCred(idx, field) {
    return (e) => setCredentials((prev) => prev.map((c, i) =>
      i === idx ? { ...c, [field]: e.target.value } : c
    ));
  }
  function removeCred(idx) {
    setCredentials((prev) => prev.filter((_, i) => i !== idx));
  }
  function addCred() {
    setCredentials((prev) => [...prev, { username: '', password: '', label: '' }]);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const body = {
        contact_id: record.id,
        sftp_protocol: shared.sftp_protocol,
        sftp_host:     shared.sftp_host,
        sftp_port:     shared.sftp_port === '' ? null : Number(shared.sftp_port),
        date_received: shared.date_received || null,
        credentials:   credentials.filter((c) => c.username || c.password),
        raw_email_text: text
      };
      // When opened from a specific site, attach the configuration to
      // that site directly instead of letting the server fall back to
      // the customer's first site.
      if (site && site.id) body.site_id = site.id;
      const res = await axios.post('/api/bulloch/configuration', body);
      setSavedCount(res.data.credential_ids?.length ?? credentials.length);
      setStage('saved');
      if (onSaved) onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  // Site number for the mismatch check — prefer the per-site context
  // when the dialog was opened from a Site row, otherwise fall back
  // to the customer's legacy site_number summary field.
  const recordSite = (site?.site_number) || record?.site_number || null;
  const siteMismatch = siteFromSubject && recordSite && siteFromSubject !== recordSite;
  const canSave = credentials.some((c) => c.username || c.password);

  return (
    <div className="modal-overlay" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !parsing && !saving) onClose();
    }}>
      <div className="modal-card" role="dialog" aria-modal="true" aria-label="Parse Bulloch Email">
        <header className="modal-head">
          <h3>
            {stage === 'paste'  && 'Parse Bulloch Email'}
            {stage === 'review' && 'Review Parsed Configuration'}
            {stage === 'saved'  && 'Configuration Saved'}
          </h3>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={parsing || saving}
            aria-label="Close"
          >×</button>
        </header>

        <div className="modal-body">
          {stage === 'paste' && (
            <>
              <p className="modal-sub">
                Paste the Bulloch reply email body below. The parser detects
                {' '}<strong>multiple Username / Password pairs</strong> and
                auto-labels by suffix — <code>-bk</code> → Back Office,
                {' '}<code>-pcp</code> → PetroCan.
              </p>
              <textarea
                className="bulloch-parse-textarea"
                rows={14}
                placeholder={`e.g.

Protocol : SFTP
Host     : sftp.bullochtech.com
Port     : 24

Username : ess304141-bk
Password : <secret>

Username : ess304141-pcp
Password : <secret>`}
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={parsing}
                autoFocus
              />
              {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={onClose} disabled={parsing}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleParse}
                  disabled={parsing || !text.trim()}
                >
                  {parsing ? 'Parsing…' : 'Parse'}
                </button>
              </div>
            </>
          )}

          {stage === 'review' && (
            <>
              <p className="modal-sub">
                Detected <strong>{credentials.length}</strong>{' '}
                credential set{credentials.length === 1 ? '' : 's'}. Edit any
                field below, then confirm to save.
              </p>

              {siteMismatch && (
                <p className="error" style={{
                  background: 'var(--color-warn-bg)',
                  color: 'var(--color-warn)',
                  borderColor: 'var(--color-warn-border)'
                }}>
                  <strong>Site number mismatch.</strong> Email subject references
                  site <code>{siteFromSubject}</code>, but this customer's site is
                  {' '}<code>{recordSite}</code>. Make sure you're saving against
                  the right record.
                </p>
              )}

              <h4 className="preview-heading" style={{ marginTop: 0 }}>
                Shared connection
              </h4>
              <div className="form-grid">
                <label className="form-field">
                  <span>Protocol</span>
                  <input type="text" value={shared.sftp_protocol} onChange={updateShared('sftp_protocol')} className="cell-mono" />
                </label>
                <label className="form-field">
                  <span>Host</span>
                  <input type="text" value={shared.sftp_host} onChange={updateShared('sftp_host')} className="cell-mono" />
                </label>
                <label className="form-field">
                  <span>Port</span>
                  <input type="number" value={shared.sftp_port} onChange={updateShared('sftp_port')} className="cell-mono" />
                </label>
                <label className="form-field">
                  <span>Date Received</span>
                  <DateInput value={shared.date_received} onChange={updateShared('date_received')} className="cell-mono" />
                </label>
              </div>

              <h4 className="preview-heading" style={{ marginTop: 'var(--space-5)' }}>
                Credential sets
              </h4>
              {credentials.length === 0 && (
                <p className="empty-tab">No credentials parsed.</p>
              )}
              {credentials.map((c, i) => (
                <div key={i} className="cred-edit-card">
                  <div className="cred-edit-card-head">
                    <strong>{c.label || 'Unlabeled'}</strong>
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => removeCred(i)}
                      style={{ color: 'var(--color-bad)' }}
                    >
                      Remove
                    </button>
                  </div>
                  <div className="form-grid">
                    <label className="form-field">
                      <span>Label</span>
                      <input type="text" value={c.label} onChange={updateCred(i, 'label')} placeholder="Back Office / PetroCan / …" />
                    </label>
                    <label className="form-field">
                      <span>Username</span>
                      <input type="text" value={c.username} onChange={updateCred(i, 'username')} className="cell-mono" autoComplete="off" />
                    </label>
                    <label className="form-field">
                      <span>Password</span>
                      <input type="text" value={c.password} onChange={updateCred(i, 'password')} className="cell-mono" autoComplete="off" />
                    </label>
                  </div>
                </div>
              ))}
              <button type="button" className="btn-secondary btn-sm" onClick={addCred} style={{ marginTop: 'var(--space-3)' }}>
                + Add credential set
              </button>

              {error && <p className="error" style={{ marginTop: 'var(--space-3)' }}>{error}</p>}

              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setStage('paste')} disabled={saving}>
                  Back to Paste
                </button>
                <button type="button" className="btn-primary" onClick={handleSave} disabled={saving || !canSave}>
                  {saving ? 'Saving…' : `Confirm & Save (${credentials.length})`}
                </button>
              </div>
            </>
          )}

          {stage === 'saved' && (
            <div className="quote-success">
              <div className="quote-success-icon">✓</div>
              <h4>Configuration Saved</h4>
              <p>
                {savedCount} credential set{savedCount === 1 ? '' : 's'} populated for
                {' '}<strong>{record?.company || `Customer #${record?.id}`}</strong>.
                A note was logged on the customer record.
              </p>
              <div className="form-actions">
                <button type="button" className="btn-primary" onClick={onClose}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
